import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef
} from 'react';
import useTranslations from '../../../hooks/useTranslations';
import { FormContext, useField } from '../FormContext';
import MultiSelectFieldView from '../view/MultiSelectFieldView';

const wrapValues = state => {
  const values = [];
  state.forEach((checked, val) => {
    if (checked) {
      values.push(val);
    }
  });
  return values.length ? `||${values.join('||')}||` : null;
};

const unWrapValues = (options = [], checked) => {
  checked = checked || '';
  const checkedArr = checked.split('||').filter(v => v !== '');

  const values = new Map();
  options.forEach(v => {
    values.set(v, false);
  });
  checkedArr.forEach(v => values.set(v, true));

  return values;
};

const MultiSelectReducer = (state, action) => {
  const { type, payload, index } = action;

  switch (type) {
    case 'set':
      return payload;

    case 'update':
      const newMap = new Map(state);
      Object.entries(payload).forEach(i => newMap.set(i[0], i[1]));
      return newMap;

    default:
      return state;
  }
};

const MultiSelectFieldEngine = ({
  internalId,
  value,
  setValue,
  required,
  readOnly,
  options,
  annotations,
  ...fieldProps // for future development, know whe have this
}) => {
  const { validateField, onFormBuilderDefaultValueSaved = () => {} } =
    useContext(FormContext);
  const t = useTranslations();
  const [data, dispatch] = useReducer(
    MultiSelectReducer,
    unWrapValues(options, value)
  );
  const skipRender = useRef(false); // avoid unnecessary value renders due to options update
  const firstRender = useRef(true);
  useEffect(() => {
    skipRender.current = true;
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    dispatch({
      type: 'set',
      payload: unWrapValues(options, value)
    });
  }, [options]);

  const onChecked = useCallback(event => {
    const { name, checked } = event.target;

    dispatch({
      type: 'update',
      payload: { [name]: checked }
    });
  }, []);

  useEffect(() => {
    if (skipRender.current) {
      skipRender.current = false;
      return;
    }
    setValue(wrapValues(data));
    onFormBuilderDefaultValueSaved(wrapValues(data));
    validateField(internalId, wrapValues(data));
  }, [data]);

  const validator = useCallback(
    value => {
      value = value || '';
      let error = '';

      if (required && (value.trim() === '' || value === '||||')) {
        error = t('forms.fields.error.required');
      }

      return error;
    },
    [required]
  );

  useField(internalId, validator);

  let tooltips = {};
  if (annotations && annotations.tooltips) {
    tooltips = annotations.tooltips;
  }

  const viewProps = {
    internalId,
    data,
    onChecked,
    required,
    readOnly,
    tooltips
  };

  return <MultiSelectFieldView {...viewProps} />;
};

export default MultiSelectFieldEngine;
